import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Languages, ChevronDown, Check } from 'lucide-react';

const LANGUAGES = [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'es', name: 'Spanish', nativeName: 'Español' }
];

export const LanguageSelector: React.FC = () => {
    const { i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Filter to find the current language, matching by prefix (e.g., 'en-US' matches 'en')
    const currentLanguage = LANGUAGES.find(l => i18n.language.startsWith(l.code)) || LANGUAGES[0];

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLanguageChange = (code: string) => {
        i18n.changeLanguage(code);
        localStorage.setItem('i18nextLng', code);
        setIsOpen(false);
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 hover:border-violet-300 hover:bg-violet-50 transition-all text-slate-600 hover:text-violet-600 font-medium text-sm group"
                aria-label="Select Language"
            >
                <Languages className="w-4 h-4 text-slate-400 group-hover:text-violet-500 transition-colors" />
                <span className="min-w-[24px]">{currentLanguage.nativeName}</span>
                <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-100 py-2 z-50 animate-in fade-in zoom-in-95 duration-200 origin-top-right">
                    {LANGUAGES.map((lang) => (
                        <button
                            key={lang.code}
                            onClick={() => handleLanguageChange(lang.code)}
                            className={`w-full flex items-center justify-between px-4 py-2 text-sm transition-colors ${currentLanguage.code === lang.code
                                ? 'bg-violet-50 text-violet-600 font-semibold'
                                : 'text-slate-600 hover:bg-slate-50'
                                }`}
                        >
                            <div className="flex flex-col items-start">
                                <span className="text-[10px] text-slate-400 font-normal uppercase tracking-wider">{lang.name}</span>
                                <span>{lang.nativeName}</span>
                            </div>
                            {currentLanguage.code === lang.code && <Check className="w-4 h-4 text-violet-500" />}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};
